import type { PropsWithChildren } from "react";
import { useEffect, useRef } from "react";
import { css, styled } from "styled-components";

import { zIndex } from "common/constants/zIndex";

type CoachmarkOverlayWrapperProps = PropsWithChildren & { isActive: boolean };

export const CoachmarkOverlayWrapper = ({ children, isActive }: CoachmarkOverlayWrapperProps) => {
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleDocumentClick = (event: MouseEvent) =>
      wrapperRef.current?.contains(event.target as Node) ? undefined : event.preventDefault();

    if (isActive) document.addEventListener("click", handleDocumentClick, true);
    else document.removeEventListener("click", handleDocumentClick, true);

    return () => document.removeEventListener("click", handleDocumentClick, true);
  }, [isActive]);

  return (
    <StyledWrapper isActive={isActive}>
      <StyledContentWrapper ref={wrapperRef}>
        <StyledOverlay />
        {children}
      </StyledContentWrapper>
    </StyledWrapper>
  );
};

const StyledWrapper = styled.div<{ isActive: boolean }>`
  position: fixed;
  right: 0;
  bottom: 0;
  left: 100%;
  z-index: ${zIndex.coachmarkOverlayWrapper};
  transition:
    all 0.375s ease,
    left 0s 1s ease;
  overflow: hidden;
  top: 0;

  ${({ isActive }) =>
    isActive &&
    css`
      transition:
        all 0.375s ease,
        left 0s ease;
      left: 0;
    `};
`;

const StyledContentWrapper = styled.div`
  display: flex;
  -webkit-overflow-scrolling: touch;
  inset: 0;
  position: absolute;
  z-index: 1;
  align-items: flex-start;
  overflow-x: hidden;
  overflow-y: auto;
`;

const StyledOverlay = styled.div`
  inset: 0;
  position: absolute;
  z-index: 0;
`;
